/**
 * IdeaStarRating
 *
 * Five-star input for the client-portal idea review. Tap targets are
 * ≥44px so the control is thumb-friendly on phones. Tapping the same
 * star that is currently the high-water mark clears the rating.
 */

import React from "react";
import { StarIcon } from "@/components/icons";

interface IdeaStarRatingProps {
  /** Current rating (1-5) or null when unrated. */
  value: number | null;
  /** Called with the new rating (1-5) or null to clear. */
  onChange: (value: number | null) => void;
  /** Disable interaction while a write is in flight. */
  disabled?: boolean;
  /** Visual size; controls icon dimensions, hit targets stay ≥44px. */
  size?: "sm" | "md" | "lg";
  /** A11y label for the whole group. */
  ariaLabel?: string;
}

const ICON_SIZE: Record<NonNullable<IdeaStarRatingProps["size"]>, string> = {
  sm: "w-5 h-5",
  md: "w-6 h-6",
  lg: "w-7 h-7",
};

export const IdeaStarRating: React.FC<IdeaStarRatingProps> = ({
  value,
  onChange,
  disabled = false,
  size = "md",
  ariaLabel = "Rate this idea",
}) => {
  const handleClick = (rating: number) => {
    if (disabled) return;
    // Tap the current high-water mark to clear; otherwise set to that rating.
    onChange(value === rating ? null : rating);
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="inline-flex items-center -mx-1"
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = value !== null && star <= value;
        return (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={`${star} star${star === 1 ? "" : "s"}`}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              handleClick(star);
            }}
            // 44×44 hit target via w-11 h-11; the icon inside is smaller.
            // The negative -mx-1 on the parent + p-2.5 (10px) gives ~24px
            // of spacing between star centres, comfortable on a thumb.
            className={`inline-flex items-center justify-center w-11 h-11 rounded-md transition-colors group disabled:opacity-50 disabled:cursor-not-allowed ${
              filled
                ? "text-amber-500"
                : "text-gray-300 hover:text-amber-400"
            }`}
          >
            <StarIcon
              className={`${ICON_SIZE[size]} transition-transform group-active:scale-90`}
              filled={filled}
            />
          </button>
        );
      })}
    </div>
  );
};
